import React from 'react';
import styled from 'styled-components';
import { Trans } from 'react-i18next';
import toast from 'react-hot-toast';

import { AUDIENCES } from 'helpers/enums';
import { setMultipleKeys } from 'hooks/useSessionStorage';
import { APPLICATION_PROMOTE } from '../../../../dal/applications';
import LocationList from '../../../locations-list';

const PROMOTION_ERROR_CONDITIONS = {
  INACTIVE_LINK: ({ confirmDialog, onFinished, showingId, navigate, userContext }) => {
    confirmDialog({
      width: '95%',
      height: '95%',
      contentStyle: { overflow: 'auto' },
      description: ({ handleCancel }) => {
        const [saving, setSaving] = React.useState(false);

        return (
          <LocationWrapper>
            <div className="title">
              <strong>
                <Trans i18nKey="promote_dialog.unavailable_location.title">
                  Location is no longer available
                </Trans>
              </strong>
              <Trans i18nKey="promote_dialog.unavailable_location.description">
                This location is not currently taking applications for rent, however you can choose
                a different location from below.
              </Trans>
            </div>
            <LocationList
              audienceOverride={AUDIENCES.applicant.uuid}
              hideHeader={true}
              onBack={() => {
                handleCancel?.();
                onFinished?.();
              }}
              onSelect={async (location) => {
                if (saving) return;
                setSaving(true);
                promoteApp({
                  confirmDialog,
                  locationId: location._id,
                  userContext,
                  showingId: showingId,
                  navigate,
                  onFinished: () => {
                    setSaving(false);
                    handleCancel?.();
                    onFinished?.();
                  }
                });
              }}
            />
          </LocationWrapper>
        );
      },
      hideConfirm: true,
      onCancel: () => onFinished?.(),
      cancelWidth: 100,
      cancelLabel: <Trans i18nKey="buttonsActions.cancel">Cancel</Trans>
    });
  }
};

export const promoteApp = async ({
  confirmDialog,
  userContext,
  locationId,
  showingId,
  navigate,
  onFinished
}) => {
  try {
    let existingApplication = await APPLICATION_PROMOTE({
      userContext,
      applicationId: showingId,
      data: { locationId }
    });
    const urlBase = `/${existingApplication.companyId}/${AUDIENCES.applicant.routePrefix}`;
    const path = AUDIENCES.applicant.paths.detail(existingApplication._id);
    onFinished?.();
    navigate(`${urlBase}${path}`, null, true);
  } catch (err) {
    console.error(err, err?.data);

    const errorCode = err?.data?.code;
    if (errorCode && PROMOTION_ERROR_CONDITIONS[errorCode]) {
      PROMOTION_ERROR_CONDITIONS[errorCode]({
        confirmDialog,
        onFinished,
        showingId,
        navigate,
        userContext
      });
      return;
    }
    const message = ['Failed to create application', err.data?.reason].filter(Boolean).join(': ');
    toast.error(message);
    onFinished?.();
  }
};

export const continueLeaseApplication = async ({
  confirmDialog,
  userContext,
  showing,
  existingApplications,
  navigate,
  onFinished
}) => {
  const contextToSet = {};
  if (showing.locationId) contextToSet.contextId = showing.locationId;
  if (showing.location?.companyId) contextToSet.orgId = showing.location?.companyId;
  setMultipleKeys(contextToSet);

  const urlBase = `/${contextToSet.orgId}/${AUDIENCES.applicant.routePrefix}`;
  let path = AUDIENCES.applicant.paths.create;

  // just grab the first one for now
  let existingApplication = existingApplications?.[0];

  // no app, lets create and promote!
  if (!existingApplication) {
    promoteApp({
      confirmDialog,
      userContext,
      locationId: showing.locationId,
      showingId: showing._id,
      navigate,
      onFinished
    });
    return;
  }

  if (existingApplication) {
    path = AUDIENCES.applicant.paths.detail(existingApplication._id);
    if (existingApplication?.submitted) {
      path = `${path}/summary`;
    }
  }

  onFinished?.();
  return navigate(`${urlBase}${path}`, null, true);
};
const LocationWrapper = styled.div`
  width: 100%;
  height: 100%;

  .title {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 15px;
    > strong {
      font-size: 1.5em;
    }
    margin-top: 10px;
    margin-bottom: 15px;
  }
`;
